/* eslint-disable @typescript-eslint/no-explicit-any */
import { createRef, useEffect, useRef } from 'react';
import { filter } from 'es-toolkit/compat';
import clsx from 'clsx';

import { consumer } from '@tock/coretypes';
import { getSettings } from 'settings/AppSettings';
import { useAppSettings } from 'selectors/settingsSelector';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';
import BaseComponent from '@tock/shared/components/BaseComponent';
import Lazy from '@tock/shared/components/Lazy';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';
import ScriptLoader from '@tock/shared/components/render-prop-components/ScriptLoader';
import googleMaps from '@tock/shared/utils/googleMaps';

interface Props {
  addressFields: consumer.ILocation;
  analyticsEventCategory?: string;
  businessDomainName: string;
  businessName: string;
  isAtBusinessLocation?: boolean;
  googlePlacesId?: string | null;
  lazy: boolean;
  modifierClassName?: string;
}

interface State {
  googlePlacesId?: string;
  attemptedGooglePlaceIdFetch: boolean;
}

const googleMapElementId = 'googlemap';

// TODO(visgl-burn-in): remove the legacy `BusinessMap` + `LegacyBusinessMapWithScript`
// classes and the flag fork in `BusinessMapWithScript` below (keep `BusinessMapEmbed`)
// once CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out. The file-level
// `no-explicit-any` disable (L1) and the `no-console` / `no-non-null-assertion`
// disables in these two classes go away with them.
class BusinessMap extends BaseComponent<Props, State> {
  googleMapsIframe: any;

  constructor(props: Props) {
    // eslint-disable-next-line no-console
    console.log('Rendering map');
    super(props);
    this.googleMapsIframe = createRef();
    this.state = {
      attemptedGooglePlaceIdFetch: false,
    };
  }

  componentDidMount() {
    this.fetchGooglePlaceId();
    window.addEventListener('blur', this.handleWindowBlur);

    // Send an analytics event when the map loads. We can use
    // componentDidMount() to track views since the map is lazy-loaded
    window.analytics.sendEvent(
      this.props.analyticsEventCategory,
      'Map loaded in view',
      `${this.props.businessName}`
    );
  }

  componentWillUnmount() {
    window.removeEventListener('blur', this.handleWindowBlur);
  }

  fetchGooglePlaceId = () => {
    if (typeof window.google === 'undefined') {
      return;
    }

    const { addressFields, businessName, isAtBusinessLocation, googlePlacesId } = this.props;
    if (googlePlacesId) {
      this.setState({
        attemptedGooglePlaceIdFetch: true,
        googlePlacesId,
      });
      return;
    }

    const query = filter([
      isAtBusinessLocation ? businessName : addressFields.name,
      addressFields.address,
      addressFields.city,
      addressFields.state,
      addressFields.country,
      addressFields.zipCode,
    ]).join(' ');
    const request = {
      query,
      fields: ['place_id'],
    };
    // eslint-disable-next-line no-console
    console.log('Creating PlacesService');
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    const callback = (results: any, status: any) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        const googlePlacesId = results[0] ? results[0].place_id : undefined;
        this.setState({ googlePlacesId });
      }

      this.setState({
        attemptedGooglePlaceIdFetch: true,
      });
    };

    service.findPlaceFromQuery(request, callback);
  };

  handleWindowBlur = () => {
    // Send an analytics event when the map iframe element is focused.
    // This is semi-reliable way to track clicks on a third-party iframe,
    // but it only tracks the first click (or future clicks if the user
    // focuses back out of the iframe by clicking anywhere outside the iframe).
    if (document.activeElement === this.googleMapsIframe.current) {
      window.analytics.sendEvent('Business page map', 'Map focused', `${this.props.businessName}`);
    }
  };

  render() {
    const { businessName, businessDomainName, addressFields } = this.props;
    const { googlePlacesId, attemptedGooglePlaceIdFetch } = this.state;

    if (!attemptedGooglePlaceIdFetch) {
      return null;
    }

    const encodedAddresString = googleMaps.getAddressString(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addressFields.address!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addressFields.city!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addressFields.state!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addressFields.country!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addressFields.zipCode!
    );

    const businessUrl = `https://www.exploretock.com/${businessDomainName}`;
    const encodedBusinessName = encodeURIComponent(businessName);
    const query = googlePlacesId ? `place_id:${googlePlacesId}` : encodedAddresString;

    const appSettings = getSettings(__ENV__);

    return (
      <iframe
        id={googleMapElementId}
        width="350"
        height="350"
        frameBorder="0"
        ref={this.googleMapsIframe}
        style={{ border: 0 }}
        src={`https://www.google.com/maps/embed/v1/place?key=${appSettings.GOOGLE_MAPS_KEY}&q=${query}&attribution_source=${encodedBusinessName}+on+Tock&attribution_web_url=${businessUrl}`}
        title={`Map for ${businessName}`}
        // eslint-disable-next-line no-console
        onLoad={() => console.log('Finished iFrame script load')}
        allowFullScreen
        data-testid="google-map-business"
        role="application"
      />
    );
  }
}

interface WithSriptState {
  mapsLoaded: boolean;
  scripts: { [id: string]: string };
}

class LegacyBusinessMapWithScript extends PureBaseComponent<Props, WithSriptState> {
  constructor(props: Props) {
    super(props);
    if (BROWSER) {
      (window as any).businessMapCallback = this.handleMapsLoaded;
    }

    if (this.googlePlacesIsLoaded()) {
      this.state = {
        mapsLoaded: true,
        scripts: {
          googleMapsJS: this.getGoogleScriptString(),
        },
      };
    } else {
      this.state = {
        mapsLoaded: false,
        scripts: {},
      };
    }
  }

  googlePlacesIsLoaded() {
    return (
      BROWSER &&
      typeof (window as any).google === 'object' &&
      typeof (window as any).google.maps === 'object' &&
      typeof (window as any).google.maps.places === 'object'
    );
  }

  getGoogleScriptString() {
    const appSettings = getSettings(__ENV__);
    return `https://maps.googleapis.com/maps/api/js?key=${appSettings.GOOGLE_MAPS_KEY}&libraries=places&callback=businessMapCallback`;
  }

  componentDidMount() {
    const { mapsLoaded } = this.state;
    if (BROWSER && !mapsLoaded) {
      this.setState({
        scripts: {
          googleMapsJS: this.getGoogleScriptString(),
        },
      });
    }
  }

  handleMapsLoaded() {
    // eslint-disable-next-line no-console
    console.log('Finished Places script load');
    this.setState({
      mapsLoaded: true,
    });
  }

  render() {
    const mapContainerClassName = clsx(this.props.modifierClassName || 'ProfileMeta-mapContainer');
    const { lazy } = this.props;
    const { scripts, mapsLoaded } = this.state;

    if (!lazy && scripts.googleMapsJS) {
      return (
        <ScriptLoader scripts={scripts} retryAttempts={2}>
          <div className={mapContainerClassName}>
            {mapsLoaded && this.googlePlacesIsLoaded() && <BusinessMap {...this.props} />}
          </div>
        </ScriptLoader>
      );
    }

    if (scripts.googleMapsJS) {
      return (
        <Lazy
          preload={false}
          orElse={
            <div className={clsx(this.props.modifierClassName || 'ProfileMeta-mapContainer')} />
          }
        >
          <ScriptLoader scripts={scripts} retryAttempts={2}>
            <div className={mapContainerClassName}>
              {mapsLoaded && this.googlePlacesIsLoaded() && <BusinessMap {...this.props} />}
            </div>
          </ScriptLoader>
        </Lazy>
      );
    }

    return null;
  }
}

/**
 * vis.gl (flag-ON) business map. Keeps the Google Maps **Embed** iframe exactly
 * as before, but drops the deprecated JSONP `ScriptLoader` +
 * `window.businessMapCallback` + `PlacesService.findPlaceFromQuery`. The Embed
 * API accepts either a `place_id:` query (when `googlePlacesId` is supplied) or a
 * plain address string, so no Maps JS SDK is needed just to render the map.
 */
const BusinessMapEmbed: React.FunctionComponent<Props> = ({
  addressFields,
  analyticsEventCategory,
  businessDomainName,
  businessName,
  googlePlacesId,
  modifierClassName,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const settings = useAppSettings();

  useEffect(() => {
    window.analytics.sendEvent(analyticsEventCategory, 'Map loaded in view', `${businessName}`);
    const handleWindowBlur = () => {
      if (document.activeElement === iframeRef.current) {
        window.analytics.sendEvent('Business page map', 'Map focused', `${businessName}`);
      }
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, [analyticsEventCategory, businessName]);

  const encodedAddressString = googleMaps.getAddressString(
    addressFields.address ?? '',
    addressFields.city ?? '',
    addressFields.state ?? '',
    addressFields.country ?? '',
    addressFields.zipCode ?? ''
  );

  const businessUrl = `https://www.exploretock.com/${businessDomainName}`;
  const encodedBusinessName = encodeURIComponent(businessName);
  const query = googlePlacesId ? `place_id:${googlePlacesId}` : encodedAddressString;
  const mapContainerClassName = clsx(modifierClassName || 'ProfileMeta-mapContainer');

  return (
    <div className={mapContainerClassName}>
      <iframe
        id={googleMapElementId}
        width="350"
        height="350"
        frameBorder="0"
        ref={iframeRef}
        style={{ border: 0 }}
        loading="lazy"
        src={`https://www.google.com/maps/embed/v1/place?key=${settings.GOOGLE_MAPS_KEY}&q=${query}&attribution_source=${encodedBusinessName}+on+Tock&attribution_web_url=${businessUrl}`}
        title={`Map for ${businessName}`}
        allowFullScreen
        data-testid="google-map-business"
        role="application"
      />
    </div>
  );
};

/**
 * Flag-fork for the business landing-page/receipt map. Flag-OFF keeps the legacy
 * JSONP + `PlacesService` loader; flag-ON renders the Embed iframe directly.
 */
const BusinessMapWithScript: React.FunctionComponent<Props> = (props) => {
  // TODO(visgl-burn-in): remove this flag fork and the `LegacyBusinessMapWithScript`
  // branch (keep `BusinessMapEmbed`) once CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is
  // fully rolled out.
  const visglEnabled = useIsVisglMapsEnabled();
  return visglEnabled ? (
    <BusinessMapEmbed {...props} />
  ) : (
    <LegacyBusinessMapWithScript {...props} />
  );
};

export default BusinessMapWithScript;
